import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SermonCard } from "@/components/SermonCard";
import { ArrowRight, Play, Heart, Users, BookOpen, Sparkles } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_covenant-ministry/artifacts/jzwdtaad_ChatGPT%20Image%20Jun%2012%2C%202026%2C%2008_17_31%20PM.png";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxODd8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBncm91cCUyMGRpdmVyc2UlMjBwZW9wbGUlMjBzbWlsaW5nJTIwb3V0c2lkZXxlbnwwfHx8fDE3ODA4MDA0Nzd8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [sermons, setSermons] = useState([]);
  const [live, setLive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get("/sermons"), api.get("/live")]);
        setSermons(s.data.slice(0, 3));
        setLive(l.data);
      } catch {}
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      {/* HERO — image renders at natural aspect on every screen; never crops */}
      <section className="relative w-full overflow-hidden bg-[#1B3A66] pt-20 md:pt-24">
        <img
          src={HERO_IMG}
          alt="Christian Covenant Network — A place to belong, wherever you are"
          className="block w-full h-auto"
        />
        {/* Soft bottom gradient so the CTA buttons sit cleanly on the navy band */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1B3A66] via-[#1B3A66]/50 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 z-10 text-white pb-4 sm:pb-8 md:pb-14">
          <div className="ccn-container">
            <div className="flex flex-wrap gap-2 sm:gap-4 animate-fade-up" style={{ animationDelay: '0.3s' }}>
              {(() => {
                const externalUrl = live?.watch_live_url?.trim();
                const label = (
                  <>
                    {live?.is_live && <span className="w-2 h-2 rounded-full bg-white animate-pulse-live" />}
                    {live?.is_live ? "We're live now" : "Watch Live"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                );
                return externalUrl ? (
                  <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-watch-live-btn"
                    className="ccn-btn-primary"
                  >
                    {label}
                  </a>
                ) : (
                  <Link to="/live" data-testid="hero-watch-live-btn" className="ccn-btn-primary">
                    {label}
                  </Link>
                );
              })()}
              <Link to="/sermons" data-testid="hero-sermons-btn" className="ccn-btn-ghost border-white/30 text-white hover:bg-white/10 hover:border-white">
                <Play className="w-4 h-4" /> Sermons
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES BENTO */}
      <section className="ccn-section ccn-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <div className="md:col-span-5 ccn-card p-10 flex flex-col justify-between min-h-[320px]">
            <div className="ccn-overline mb-5">What we believe</div>
            <h2 className="ccn-h2 text-[#1E1E1E]">Christ-centered. <br/>Spirit-empowered. <br/>People-loving.</h2>
            <Link to="/about" data-testid="values-readmore" className="mt-8 inline-flex items-center gap-2 text-[#C8664D] font-medium hover:gap-3 transition-all">
              Read our beliefs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
            {[
              { icon: BookOpen, title: "Scripture", text: "Anchored in the whole counsel of God." , c: '#C8664D' },
              { icon: Heart, title: "Worship", text: "Awe-filled, expressive, life-shaping.", c: '#DDA754' },
              { icon: Users, title: "Community", text: "We are family — across continents.", c: '#738C7A' },
              { icon: Sparkles, title: "Mission", text: "Sent to bless our neighborhoods and beyond.", c: '#2A2E2C' },
            ].map((v) => (
              <div key={v.title} className="ccn-card p-7" data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: v.c + '20', color: v.c }}>
                  <v.icon className="w-6 h-6" />
                </div>
                <div className="font-heading text-2xl text-[#1E1E1E] mb-2">{v.title}</div>
                <div className="text-sm text-[#575A58] leading-relaxed">{v.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERMONS */}
      <section className="ccn-section bg-[#F2EBE5]/40">
        <div className="ccn-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="ccn-overline mb-3">Latest teaching</div>
              <h2 className="ccn-h2 text-[#1E1E1E] max-w-xl">Sermons to walk with you through the week.</h2>
            </div>
            <Link to="/sermons" data-testid="sermons-viewall-btn" className="ccn-btn-ghost">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sermons.map((s) => <SermonCard key={s.id} sermon={s} />)}
          </div>
        </div>
      </section>

      {/* GIVE CTA */}
      <section className="ccn-section ccn-container">
        <div className="ccn-card overflow-hidden grid grid-cols-1 md:grid-cols-2">
          <div className="p-10 lg:p-16 flex flex-col justify-center">
            <div className="ccn-overline mb-5">Partner with us</div>
            <h2 className="ccn-h2 text-[#1E1E1E] mb-6">Your giving fuels the mission.</h2>
            <p className="text-[#575A58] mb-8 leading-relaxed max-w-md">
              Tithes and offerings empower this online ministry — sermon production, missions,
              community outreach, and care for those in need.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/give" data-testid="home-give-now-btn" className="ccn-btn-primary">
                <Heart className="w-4 h-4" /> Give Now
              </Link>
              <Link to="/about" className="ccn-btn-ghost">Learn more</Link>
            </div>
          </div>
          <div className="relative min-h-[320px]">
            <img src={COMMUNITY_IMG} alt="community" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#FAFAF8]/30" />
          </div>
        </div>
      </section>

      {/* TEACHING IMAGE BAND — CCN logo + commissioning verse */}
      <section className="relative overflow-hidden mb-32 bg-[#1B3A66]">
        <div className="ccn-container py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <img
                src="https://customer-assets.emergentagent.com/job_covenant-ministry/artifacts/fy3ha3jd_Christian%20Covenant%20Round%20LOGO.png"
                alt="Christian Covenant Network logo"
                data-testid="commissioning-logo"
                className="w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full object-cover shadow-2xl ring-4 ring-[#DDA754]/40"
              />
            </div>
            <div className="lg:col-span-7 text-white text-center lg:text-left">
              <div className="ccn-overline text-[#DDA754] mb-5">A word for the season</div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-snug">
                "And they went forth, and preached every where, the Lord working with them, and confirming the word with signs following. Amen."
              </h2>
              <div className="mt-6 text-sm tracking-[0.25em] uppercase text-white/70">Mark 16:20</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
